import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import {
  AlarmType,
  KlydoUpdate,
  PopulatedKlydo,
  SoundMode,
} from '../../../api/klydo';
import useAnalytics from '../../../hooks/useAnalytics';
import useAutoSave from '../../hooks/useAutosave';
import { DEFAULT_PLAY_ONCE_FREQUENCY } from '../components/KlydoSoundPlaybackRadio';
import useUpsertDraftKlydoMutation from './useUpsertDraftKlydoMutation';
import useRequestPublicationMutation from './useRequestPublicationMutation';
import {
  PublicationDestination,
  PublishSelection,
} from '../Publication/PublishModal';
import { PublishSuccess } from '../Publication/PublishSuccessModal';
import { getPublishSuccess } from '../Publication/publicationState';
import { useTheme } from '@mui/material';
import { Theme } from '../../../types';
import { ColorValidator } from '../../../utils/contrastCheck';
import { AlertType } from '../../../design-system/DSAlert';
import {
  INVAILED_LOOP_FILE_TYPE_ALERT,
  MAX_FILE_SIZE_ALERT,
} from '../../../consts/alerts';
import useAppContext from '../../../hooks/useAppContext';
import useUploadKlydoLoopMutation from '../../hooks/useUploadKlydoLoopMutation';
import { KlydoLoopUploadTiming } from '../../../api/uploads';
import { reportKlydoLoopUploadFailure } from '../../../utils/uploads';
import { getMediaType } from '../../../utils';
import { MAX_VIDEO_SIZE_BYTES } from '../../../consts/general';
import { PROFILE_MANDATORY_FIELDS_TO_SUBMIT_KLYDO } from '../../../consts/mandatoryFields';
import { useNavigate, useLocation } from 'react-router-dom';
import useCalendarEventQuery from './useCalendarEventQuery';
import useChimeUpload from './useChimeUpload';
import useStrikeUpload from './useStrikeUpload';
import useAlarmUpload from './useAlarmUpload';
import usePalettesLocalStorage from './usePalettesLocalStorage';

import {
  isKlydoDataIncomplete,
  getKlydoMissingFieldsMessage,
  getPublicUnsupportedProperties,
} from '../../../utils/klydoValidation';

const colorValidator = new ColorValidator();

type KlydoEditableViewProps = {
  klydo?: PopulatedKlydo;
  setSuggestedColors: (theme: Theme[] | undefined) => void;
  onLocalUrlChange: (url: string) => void;
  onSecuredUrlChange: (url: string) => void;
  klydoSecureUrl: string;
  onKlydoThemeChange: (theme: Theme | undefined) => void;
  selectedTheme: Theme | undefined;
  klydoId: string | undefined;
  onSubmitKlydo: () => void;
  /**
   * Pin the editor mounted while the post-submit confirmation is on screen — the
   * refetch that follows a successful request would otherwise flip
   * `isEditableView` to false and unmount this hook's own success modal.
   */
  onKeepEditorMounted: () => void;
};

const useKlydoEditableView = ({
  klydo,
  setSuggestedColors,
  onLocalUrlChange,
  onSecuredUrlChange,
  klydoSecureUrl,
  onKlydoThemeChange,
  selectedTheme,
  klydoId,
  onSubmitKlydo,
  onKeepEditorMounted,
}: KlydoEditableViewProps) => {
  const { showAlert, isAdmin } = useAppContext();
  const {
    mutate: upsertDraftKlydoMutation,
    mutateAsync: upsertDraftKlydoAsync,
    isPending: isUpdatingKlydo,
  } = useUpsertDraftKlydoMutation();
  const {
    mutate: requestPublicationMutation,
    isPending: isRequestingPublication,
  } = useRequestPublicationMutation();
  const location = useLocation();
  const [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);
  // The Feed selection held back while the artist reads what the feed will
  // strip. Non-null means that dialog is up and this is what confirming sends.
  const [pendingFeedSelection, setPendingFeedSelection] =
    useState<
      Extract<PublishSelection, { destination: PublicationDestination.Feed }>
    >();
  /**
   * The confirmation shown after the submit dialog closes.
   *
   * `leavesEditor` is why this is not just the copy: only the Feed branch has
   * actually submitted anything, so only it hands the klydo over to the read-only
   * view — and it has to wait until this modal is dismissed, because
   * `onSubmitKlydo` unmounts the editor and the modal with it. The Collection
   * branch saved a draft and nothing more, so the artist stays in the editor.
   */
  const [submitSuccess, setSubmitSuccess] = useState<{
    copy: PublishSuccess;
    leavesEditor: boolean;
  } | null>(null);
  const [klydoTitle, setKlydoTitle] = useState(klydo?.name);
  const [klydoSubtitle, setKlydoSubtitle] = useState(klydo?.subtitle ?? '');
  const [fileName, setFileName] = useState<string>(
    klydo?.metadata?.fileName || '',
  );
  const [calendarEventId, setCalendarEventId] = useState<string | undefined>(
    klydo?.metadata?.relatedCalendarEventId,
  );

  const [soundMode, setSoundMode] = useState<SoundMode>(
    klydo?.capabilitiesV2?.animationSound?.mode ?? SoundMode.OFF,
  );

  const [playOnceFrequency, setPlayOnceFrequency] = useState<number | null>(
    klydo?.capabilitiesV2?.animationSound?.playOnceConfig?.frequency ??
      DEFAULT_PLAY_ONCE_FREQUENCY,
  );

  /** Staged only; `onApprovedSubmit` sends it with the request, which stores it. */
  const [reviewComment, setReviewComment] = useState(
    klydo?.public?.reviewStatus?.comment ?? '',
  );

  const [isChimePlaying, setIsChimePlaying] = useState<boolean>(false);
  const chimeAudioRef = useRef<HTMLAudioElement | null>(null);

  const [isStrikePlaying, setIsStrikePlaying] = useState<boolean>(false);
  const strikeAudioRef = useRef<HTMLAudioElement | null>(null);

  const {
    chimeUrl,
    chimeName,
    chimesDurationInSeconds,
    isLoadingChime,
    handleChimeFileChange,
    handleRemoveChime,
  } = useChimeUpload({
    initialChimeUrl: klydo?.chimesUrls?.[0] || '',
    initialChimeName: klydo?.metadata?.chimesFileName || '',
    initialChimesDuration: klydo?.metadata?.chimesDurationInSeconds,
    showAlert,
  });

  const {
    strikeUrl,
    strikeName,
    isLoadingStrike,
    handleStrikeFileChange,
    handleRemoveStrike,
  } = useStrikeUpload({
    initialStrikeUrl: klydo?.strikesUrls?.[0] || '',
    initialStrikeName: '',
    showAlert,
  });

  const [alarmEnabled, setAlarmEnabled] = useState<boolean>(
    !!klydo?.capabilitiesV2?.alarm,
  );

  const {
    alarmUrl,
    alarmName,
    alarmDurationInSeconds,
    isLoadingAlarm,
    handleAlarmFileChange,
    handleRemoveAlarm: removeAlarm,
  } = useAlarmUpload({
    initialAlarmUrl: klydo?.alarmUrls?.[0] || '',
    initialAlarmName: klydo?.metadata?.alarmFileName || '',
    initialAlarmDuration: klydo?.metadata?.alarmDurationInSeconds,
    showAlert,
  });

  const [isAlarmPlaying, setIsAlarmPlaying] = useState<boolean>(false);
  const alarmAudioRef = useRef<HTMLAudioElement | null>(null);

  const { data: calendarEvent } = useCalendarEventQuery(calendarEventId!);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (klydo && !hasInitialized.current) {
      setKlydoTitle(klydo.name || '');
      setKlydoSubtitle(klydo.subtitle ?? '');
      setReviewComment(klydo.public?.reviewStatus?.comment ?? '');
      hasInitialized.current = true;
    }
  }, [klydo]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const [klydoDurationInSeconds, setKlydoDurationInSeconds] = useState<
    number | undefined
  >(klydo?.metadata?.klydoDurationInSeconds);
  const [klydoChecksum, setKlydoChecksum] = useState<string | undefined>(
    klydo?.metadata?.loopUrlChecksum,
  );
  // `null` means the automatic frame, and is also what the api needs to clear it.
  const [previewFrameSeconds, setPreviewFrameSeconds] = useState<number | null>(
    klydo?.metadata?.previewFrameSeconds ?? null,
  );
  const [savedTheme, setSavedTheme] = useState<Theme | undefined>(
    selectedTheme,
  );
  const { spacing, palette } = useTheme();
  const { mutate: uploadKlydoLoopMutation, isPending: isLoadingUrl } =
    useUploadKlydoLoopMutation({
      onError: () => {
        onLocalUrlChange('');
      },
      onSuccess: (response) => {
        abortControllerRef.current = new AbortController();
        onSecuredUrlChange(response.url);
        setKlydoDurationInSeconds(response.duration);
        setKlydoChecksum(response.checksum);
        // A frame belongs to the loop it was picked from.
        setPreviewFrameSeconds(null);
      },
    });

  const { user } = useAppContext();
  const navigate = useNavigate();
  const { clearPalettes } = usePalettesLocalStorage(klydoId);

  const [
    profileIncompleteErrorDialogOpen,
    setProfileIncompleteErrorDialogOpen,
  ] = useState<boolean>(false);

  const isProfileMissingFields = PROFILE_MANDATORY_FIELDS_TO_SUBMIT_KLYDO.some(
    (field) => !user?.[field],
  );

  const handleCloseErrorDialog = useCallback(() => {
    setProfileIncompleteErrorDialogOpen(false);
  }, [setProfileIncompleteErrorDialogOpen]);

  const onEditProfile = () => {
    navigate('/artist-page?edit=true');
  };

  const handleSaveTheme = useCallback((theme: Theme) => {
    setSavedTheme({ ...theme });
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const navigationState = location.state as {
      calendarEventId?: string;
    };
    if (navigationState?.calendarEventId) {
      setCalendarEventId(navigationState.calendarEventId);
    }
  }, [location.state]);

  useEffect(() => {
    colorValidator.initializeVibrantColors(klydoSecureUrl);
  }, [klydoSecureUrl]);

  const { trackEvent } = useAnalytics();

  const handleFileUpload = async (
    fileUrl: string,
    fileName: string,
    fileSizeBytes: number,
  ) => {
    trackEvent({ type: 'klydo_upload_started' });
    let timing: KlydoLoopUploadTiming = {};
    uploadKlydoLoopMutation(
      {
        file: fileUrl,
        fileName: fileName,
        fileType: getMediaType(fileUrl),
        onTiming: (uploadTiming) => {
          timing = uploadTiming;
        },
      },
      {
        onError: (error) =>
          reportKlydoLoopUploadFailure({
            error,
            timing,
            fileName,
            fileSizeBytes,
          }),
      },
    );
  };

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const validTypes = ['video/mp4', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        showAlert({
          message: INVAILED_LOOP_FILE_TYPE_ALERT,
          alertType: AlertType.ERROR,
        });
        return;
      }
      if (file.size > MAX_VIDEO_SIZE_BYTES) {
        showAlert({
          message: MAX_FILE_SIZE_ALERT,
          alertType: AlertType.ERROR,
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setFileName(file.name);
        onLocalUrlChange(reader.result as string);
        handleFileUpload(reader.result as string, file.name, file.size);
      };
      reader.readAsDataURL(file);
    },
    [showAlert, onLocalUrlChange],
  );

  const handleRemoveFile = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onLocalUrlChange('');
    onSecuredUrlChange('');
    setSuggestedColors([]);
    onKlydoThemeChange(undefined);
    setPreviewFrameSeconds(null);
  }, [
    onLocalUrlChange,
    onSecuredUrlChange,
    setSuggestedColors,
    onKlydoThemeChange,
  ]);

  // Handler to change sound mode directly - the boolean conversion
  // should be done in the UI component where the checkbox is rendered
  const handleSoundModeChange = useCallback(
    (mode: SoundMode) => {
      setSoundMode(mode);
    },
    [setSoundMode],
  );

  const handlePreviewFrameChange = useCallback(
    (offsetSeconds: number | null) => {
      setPreviewFrameSeconds(offsetSeconds);
    },
    [],
  );

  const handlePlayOnceFrequencyChange = useCallback(
    (frequency: number) => {
      setPlayOnceFrequency(frequency);
    },
    [setPlayOnceFrequency],
  );

  const handleChimePlayPause = useCallback(() => {
    if (!chimeUrl) return;

    if (isChimePlaying) {
      if (chimeAudioRef.current) {
        chimeAudioRef.current.pause();
      }
      setIsChimePlaying(false);
    } else {
      if (!chimeAudioRef.current) {
        chimeAudioRef.current = new Audio(chimeUrl);
        chimeAudioRef.current.loop = true;
      }
      chimeAudioRef.current.play().catch((error) => error);
      setIsChimePlaying(true);
    }
  }, [chimeUrl, isChimePlaying]);

  useEffect(() => {
    return () => {
      if (chimeAudioRef.current) {
        chimeAudioRef.current.pause();
        chimeAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (chimeAudioRef.current) {
      chimeAudioRef.current.pause();
      chimeAudioRef.current = null;
      setIsChimePlaying(false);
    }
  }, [chimeUrl]);

  const handleStrikePlayPause = useCallback(() => {
    if (!strikeUrl) return;

    if (isStrikePlaying) {
      if (strikeAudioRef.current) {
        strikeAudioRef.current.pause();
      }
      setIsStrikePlaying(false);
    } else {
      if (!strikeAudioRef.current) {
        strikeAudioRef.current = new Audio(strikeUrl);
        strikeAudioRef.current.loop = false;
        strikeAudioRef.current.addEventListener('ended', () =>
          setIsStrikePlaying(false),
        );
      }
      strikeAudioRef.current.play().catch((error) => error);
      setIsStrikePlaying(true);
    }
  }, [strikeUrl, isStrikePlaying]);

  useEffect(() => {
    return () => {
      if (strikeAudioRef.current) {
        strikeAudioRef.current.pause();
        strikeAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (strikeAudioRef.current) {
      strikeAudioRef.current.pause();
      strikeAudioRef.current = null;
      setIsStrikePlaying(false);
    }
  }, [strikeUrl]);

  const handleAlarmToggle = useCallback(() => {
    setAlarmEnabled((prev) => {
      const next = !prev;
      if (!next) {
        removeAlarm();
      }
      return next;
    });
  }, [removeAlarm]);

  const handleRemoveAlarm = useCallback(() => {
    removeAlarm();
  }, [removeAlarm]);

  const handleAlarmPlayPause = useCallback(() => {
    if (!alarmUrl) return;

    if (isAlarmPlaying) {
      if (alarmAudioRef.current) {
        alarmAudioRef.current.pause();
      }
      setIsAlarmPlaying(false);
    } else {
      if (!alarmAudioRef.current) {
        alarmAudioRef.current = new Audio(alarmUrl);
        alarmAudioRef.current.loop = true;
      }
      alarmAudioRef.current.play().catch((e) => e);
      setIsAlarmPlaying(true);
    }
  }, [alarmUrl, isAlarmPlaying]);

  useEffect(() => {
    return () => {
      if (alarmAudioRef.current) {
        alarmAudioRef.current.pause();
        alarmAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (alarmAudioRef.current) {
      alarmAudioRef.current.pause();
      alarmAudioRef.current = null;
      setIsAlarmPlaying(false);
    }
  }, [alarmUrl]);

  const handleSubmitToReview = useCallback(() => {
    if (!klydoId) return;
    if (isProfileMissingFields && !isAdmin) {
      setProfileIncompleteErrorDialogOpen(true);
      return;
    }
    setSubmitDialogOpen(true);
  }, [klydoId, isProfileMissingFields, isAdmin]);

  // The two autosave payloads, hoisted out of their handlers so the publication
  // flow can persist the exact same content before requesting a review (the api
  // no longer accepts content on the request — see `onApprovedSubmit`).
  const titleUpdate = useMemo<KlydoUpdate>(
    () => ({
      name: klydoTitle || 'Untitled',
      subtitle: klydoSubtitle,
    }),
    [klydoTitle, klydoSubtitle],
  );

  const handleAutoSaveTitle = useCallback(() => {
    if (!klydoId) return;

    upsertDraftKlydoMutation({
      klydoId: klydoId,
      klydo: titleUpdate,
    });
  }, [klydoId, titleUpdate, upsertDraftKlydoMutation]);

  const contentUpdate = useMemo<KlydoUpdate>(
    () => ({
      loopUrl: klydoSecureUrl,
      metadata: {
        fileName: fileName,
        klydoDurationInSeconds: klydoDurationInSeconds,
        loopUrlChecksum: klydoChecksum,
        relatedCalendarEventId:
          calendarEvent?.id || klydo?.metadata?.relatedCalendarEventId,
        chimesFileName: chimeName || '',
        chimesDurationInSeconds: chimesDurationInSeconds || 0,
        alarmFileName: alarmName || '',
        alarmDurationInSeconds: alarmDurationInSeconds || 0,
        previewFrameSeconds,
      },
      theme: savedTheme,
      capabilitiesV2: {
        animationSound: {
          mode: soundMode,
          ...(soundMode === SoundMode.FIRST_LOOP && playOnceFrequency
            ? { playOnceConfig: { frequency: playOnceFrequency, random: true } }
            : {}),
        },
        ...(alarmEnabled ? { alarm: { type: AlarmType.REMINDER } } : {}),
      },
      chimesUrls: chimeUrl ? [chimeUrl] : [],
      strikesUrls: strikeUrl ? [strikeUrl] : [],
      alarmUrls: alarmUrl ? [alarmUrl] : [],
    }),
    [
      savedTheme,
      fileName,
      klydoDurationInSeconds,
      klydoSecureUrl,
      calendarEvent,
      soundMode,
      playOnceFrequency,
      chimeUrl,
      chimeName,
      chimesDurationInSeconds,
      strikeUrl,
      klydoChecksum,
      klydo?.metadata?.relatedCalendarEventId,
      alarmEnabled,
      alarmUrl,
      alarmName,
      alarmDurationInSeconds,
      previewFrameSeconds,
    ],
  );

  const handleAutoSaveOtherFields = useCallback(() => {
    if (!klydoId) return;

    upsertDraftKlydoMutation({
      klydoId: klydoId,
      klydo: contentUpdate,
    });
  }, [klydoId, contentUpdate, upsertDraftKlydoMutation]);

  const { isSaving: isSavingDebounced } = useAutoSave({
    onSave: handleAutoSaveTitle,
    dependencies: [klydoTitle, klydoSubtitle],
    delay: 1000,
  });

  const { isSaving: isSavingImmediate } = useAutoSave({
    onSave: handleAutoSaveOtherFields,
    dependencies: [
      fileName,
      chimeName,
      savedTheme,
      soundMode,
      playOnceFrequency,
      klydoSecureUrl,
      klydoDurationInSeconds,
      chimeUrl,
      chimesDurationInSeconds,
      strikeUrl,
      klydoChecksum,
      alarmEnabled,
      alarmUrl,
      alarmName,
      alarmDurationInSeconds,
      previewFrameSeconds,
    ],
    delay: 0,
  });

  const isSaving = isSavingDebounced || isSavingImmediate;

  const isSubmitButtonDisabled = useMemo(() => {
    return (
      isKlydoDataIncomplete(klydo) ||
      isUpdatingKlydo ||
      isLoadingUrl ||
      isSaving
    );
  }, [klydo, isUpdatingKlydo, isLoadingUrl, isSaving]);

  const handleMissingFields = () => {
    return getKlydoMissingFieldsMessage(klydo, 'Missing fields: ');
  };

  const onChangeColor = useCallback(
    (key: string, color: string) => {
      const shouldSkipContrastCheck = key !== 'handsColor';
      const isChangeApplicable = colorValidator.validateColor({
        color,
        themeKey: key,
        loopUrl: klydoSecureUrl,
        selectedTheme,
        shouldSkipContrastCheck,
      });
      onKlydoThemeChange({
        ...selectedTheme,
        [key]: color,
      } as Theme);
      return isChangeApplicable || false;
    },
    [selectedTheme, klydoSecureUrl, onKlydoThemeChange],
  );

  /**
   * Read off the LOCAL edits, not off `klydo`: the publication request flushes
   * the current content before it is sent, so a chime uploaded a second ago is
   * part of what goes to review.
   */
  const publicUnsupportedProperties = useMemo(
    () =>
      getPublicUnsupportedProperties({
        hasChime: !!chimeUrl,
        hasStrike: !!strikeUrl,
        soundMode,
      }),
    [chimeUrl, strikeUrl, soundMode],
  );

  const sendFeedRequest = useCallback(
    (
      selection: Extract<
        PublishSelection,
        { destination: PublicationDestination.Feed }
      >,
    ) => {
      // Both callers already checked this; repeated so the id stays narrowed.
      if (!klydoId) return;

      requestPublicationMutation(
        {
          klydoId: klydo?.id || klydoId,
          publicationTimestamp: selection.publicationTimestamp,
          // Always sent: `''` is what clears the reviewer's note.
          comment: reviewComment.trim(),
        },
        {
          onSuccess: () => {
            setSubmitDialogOpen(false);
            setPendingFeedSelection(undefined);
            clearPalettes();
            // Before the modal goes up: the request's own `onSettled`
            // invalidates `['klydo', id]`, and that refetch comes back PENDING,
            // which is enough to unmount this editor — and the modal with it.
            onKeepEditorMounted();
            setSubmitSuccess({
              copy: getPublishSuccess(selection),
              leavesEditor: true,
            });
          },
        },
      );
    },
    [
      klydo?.id,
      klydoId,
      requestPublicationMutation,
      reviewComment,
      clearPalettes,
      onKeepEditorMounted,
    ],
  );

  const onApprovedSubmit = useCallback(
    async (selection: PublishSelection) => {
      if (!klydoId) return;

      // The publication request no longer carries the klydo — the api validates
      // the STORED document. This used to be the one call that could not miss a
      // local edit (it sent name/loopUrl/theme itself); now an unsaved edit would
      // be silently reviewed against the previous content. Autosave has usually
      // persisted everything already (the Submit button is disabled while a save
      // is pending), but "usually" is not a guarantee, so flush the current
      // content and only continue once the api has acknowledged it.
      try {
        await upsertDraftKlydoAsync({
          klydoId,
          klydo: { ...contentUpdate, ...titleUpdate },
        });
      } catch {
        // The save failed and was already surfaced by useKlydoMutation. Asking for
        // a review of stale content is worse than not asking, so stop and leave
        // the dialog open.
        return;
      }

      // Collection: the klydo is reviewed with its Collection, so there is nothing
      // to submit now. The flush above already saved it as a draft — that IS the
      // whole action, and the artist stays in the editor.
      if (selection.destination === PublicationDestination.Collection) {
        setSubmitDialogOpen(false);
        setSubmitSuccess({
          copy: getPublishSuccess(selection),
          leavesEditor: false,
        });
        return;
      }

      // Feed only, and only now that the destination is settled: the feed plays
      // no chime, strike or klydo audio, and the api clears all three when it
      // approves the request. A klydo saved for a Collection keeps them, so it
      // must never see this.
      if (publicUnsupportedProperties.length) {
        setSubmitDialogOpen(false);
        setPendingFeedSelection(selection);
        return;
      }

      sendFeedRequest(selection);
    },
    [
      klydoId,
      contentUpdate,
      titleUpdate,
      upsertDraftKlydoAsync,
      publicUnsupportedProperties.length,
      sendFeedRequest,
    ],
  );

  // The selection is held until the request SUCCEEDS (cleared in `onSuccess`),
  // so a failed request leaves this dialog up and one more Continue retries it.
  // Clearing it here dropped the artist back to the editor with nothing open and
  // no way on except re-picking the destination and the date.
  const onConfirmSpecialProperties = useCallback(() => {
    if (!pendingFeedSelection) return;
    sendFeedRequest(pendingFeedSelection);
  }, [pendingFeedSelection, sendFeedRequest]);

  // Cancelling drops the request entirely and returns the artist to the editor
  // with the sounds still on the klydo — nothing was sent.
  const onClosedSpecialPropertiesDialog = useCallback(() => {
    setPendingFeedSelection(undefined);
  }, []);

  const onCloseSubmitSuccess = useCallback(() => {
    const leavesEditor = submitSuccess?.leavesEditor;
    setSubmitSuccess(null);
    if (leavesEditor) {
      onSubmitKlydo();
    }
  }, [submitSuccess, onSubmitKlydo]);

  const onClosedSubmitDialog = useCallback(() => {
    setSubmitDialogOpen(false);
  }, []);

  const handleRemoveChimeAndStrike = useCallback(() => {
    handleRemoveChime();
    handleRemoveStrike();
  }, [handleRemoveChime, handleRemoveStrike]);

  return {
    updateKlydoMutation: upsertDraftKlydoMutation,
    handleFileChange,
    handleRemoveFile,
    handleSubmitToReview,
    reviewComment,
    setReviewComment,
    klydoTitle,
    setKlydoTitle,
    klydoSubtitle,
    setKlydoSubtitle,
    selectedTheme,
    isLoadingUrl,
    spacing,
    isSubmitButtonDisabled,
    setSuggestedColors,
    fileName,
    handleMissingFields,
    onChangeColor,
    profileIncompleteErrorDialogOpen,
    handleCloseErrorDialog,
    onEditProfile,
    onApprovedSubmit,
    publicUnsupportedProperties,
    specialPropertiesDialogOpen: !!pendingFeedSelection,
    isRequestingPublication,
    onConfirmSpecialProperties,
    onClosedSpecialPropertiesDialog,
    onClosedSubmitDialog,
    submitDialogOpen,
    submitSuccess: submitSuccess?.copy ?? null,
    onCloseSubmitSuccess,
    handleSaveTheme,
    palette,
    calendarEvent,
    soundMode,
    onSoundModeChange: handleSoundModeChange,
    playOnceFrequency,
    onPlayOnceFrequencyChange: handlePlayOnceFrequencyChange,
    chimeUrl,
    chimeName,
    handleChimeFileChange,
    handleRemoveChimeAndStrike,
    isLoadingChime,
    isChimePlaying,
    handleChimePlayPause,
    strikeUrl,
    strikeName,
    isLoadingStrike,
    handleStrikeFileChange,
    handleRemoveStrike,
    isStrikePlaying,
    handleStrikePlayPause,
    alarmEnabled,
    handleAlarmToggle,
    alarmUrl,
    alarmName,
    isLoadingAlarm,
    handleAlarmFileChange,
    handleRemoveAlarm,
    isAlarmPlaying,
    handleAlarmPlayPause,
    klydoDurationInSeconds,
    previewFrameSeconds,
    onPreviewFrameChange: handlePreviewFrameChange,
  };
};
export default useKlydoEditableView;
